import React from 'react';

import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import EditButtons, {DEFAULT_EDIT_BUTTON_STYLES, EditButtonStates} from 'metadata/EditButtons';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import ManagedColumnsSimpleTableEditable from 'notebook/simple-tables/ManagedColumnsSimpleTableEditable';
import {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import LoadingTablePlaceholder from 'notebook/tables/core-ui/LoadingTablePlaceholder';
import {TableHeaderControlsArgs} from 'notebook/tables/TableConstants';
import {RequestFullfillableStatuses} from 'requests/RequestConstants';
import {getContainerIds} from 'requests/SampleGroupUtil';
import {UIRequestTask} from 'requests/tasks/RequestTaskConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import unexpectedCallback from 'util/unexpectedCallback';
import {PreparedSampleGroup, PreparedUISampleGroup} from 'workflows/samples/PreparedSampleGroupConstants';

import {ContainerGraphQL} from '../../../graphql-fragments/inventory/__generated/ContainerGraphQL';
import {StorableSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {RequestTaskGraphQL} from '../../../graphql-fragments/requests/__generated/RequestTaskGraphQL';
import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import {SampleGroupSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/SampleGroupSchemaGraphQL';
import {convertToTable, getColumnConstraints, getColumnWidths} from './RequestSampleTableUtil';
import {TABLE_NAME} from './SamplesTableConstants';

export interface RequestSampleTableProps {
  requestId: string;
  sampleGroups: Array<PreparedSampleGroup | PreparedUISampleGroup>;
  sampleGroupSchema: SampleGroupSchemaGraphQL;
  requestTasks: Array<RequestTaskGraphQL | UIRequestTask>;
  sampleGroupIdsByRequestTaskId: {[taskId: string]: Array<string>};
  requestTaskSchemasById: {[taskSchemaId: string]: RequestTaskSchemaGraphQL};
  fulfillableStatuses: RequestFullfillableStatuses;
  containersById: {[containerId: string]: ContainerGraphQL | null};
  ancestralStorablesById: Record<string, Array<StorableSummaryGraphQL>>;
  canToggleEditMode: boolean;
  onEdit: () => void;
}

export default class RequestSampleTable extends React.Component<RequestSampleTableProps> {
  _getContainerIds(): Array<string> {
    return getContainerIds(
      this.props.sampleGroups.map((sampleGroup) => ({
        sampleGroup,
        sampleGroupSchema: graphQLToREST('SampleGroupSchema', this.props.sampleGroupSchema),
      }))
    );
  }

  _getRightHeader({isCollapsed}: TableHeaderControlsArgs): JSX.Element | null {
    if (!this.props.canToggleEditMode || isCollapsed) {
      return null;
    }

    return (
      <span className="u-display-inline-block">
        <EditButtons
          buttonStyles={{
            ...DEFAULT_EDIT_BUTTON_STYLES,
            editButton: ButtonStyle.SECONDARY,
            doneButton: ButtonStyle.SECONDARY,
            savingButton: ButtonStyle.SECONDARY,
          }}
          editingState={EditButtonStates.NOT_EDITING}
          onCancel={unexpectedCallback}
          onComplete={unexpectedCallback}
          onEdit={this.props.onEdit}
        />
      </span>
    );
  }

  _isDataMissing(): boolean {
    return this._getContainerIds().some((containerId) => this.props.containersById[containerId] == null);
  }

  override render(): JSX.Element {
    if (this._isDataMissing()) {
      // TODO(ala): handle inacessible models
      return <LoadingTablePlaceholder tableName={TABLE_NAME} />;
    }

    const {
      sampleGroups,
      sampleGroupSchema,
      requestTasks,
      requestTaskSchemasById,
      containersById,
      ancestralStorablesById,
      sampleGroupIdsByRequestTaskId,
      fulfillableStatuses,
    } = this.props;

    return (
      <div className="mediocre react-tab-container--mediocre-tableWrapper">
        <ManagedColumnsSimpleTableEditable
          initialColumnWidths={getColumnWidths({
            columnConstraints: getColumnConstraints({
              sampleGroupSchema,
              requestTasks,
              sampleGroups,
              containersById,
              requestTaskSchemasById,
              ancestralStorablesById,
              isReadOnly: false,
            }),
          })}
          tableWithoutColumnWidths={convertToTable({
            sampleGroups,
            sampleGroupSchema,
            requestTasks,
            sampleGroupIdsByRequestTaskId,
            requestTaskSchemasById,
            containersById,
            ancestralStorablesById,
            isReadOnly: true,
            fulfillableStatuses,
          })}
          {...standaloneProps}
          tableLabel={TABLE_NAME}
          cid={`request-submission-table-${this.props.requestId}`}
          locked={false}
          viewer={false}
          hideFormulaBox={true}
          enableFormulas={false}
          hasPlaceholderRows={sampleGroups.length === 0}
          headerControls={(args) => this._getRightHeader(args)}
          // storeState is invoked when selection changes
          storeState={() => {}}
          restoreState={() => {}}
          isLeftAlignColumnHeader={true}
          updateHandler={() => {}}
        />
      </div>
    );
  }
}
